/**
 * PlatformConnectorsTab — Admin Center → Platform Connectors (Platform Admin only).
 *
 * <h3>Why this exists</h3>
 * Some things the platform does are the PLATFORM's own, not a customer's: billing invoices go out
 * from {@code billing@prove7.ai}, invitations into a brand-new customer are sent by Prove7, and
 * managed-mode model calls run on Prove7's own provider key. Those must use platform-owned
 * connectors — never a paying customer's.
 *
 * This surface lets a Platform Admin add ANY connector from the marketplace as a platform
 * connector (owned by the System Tenant, the fixed platform-owned scope), then configure and test
 * it — for use across platform functions. The backend resolves platform-owned connectors by the
 * System Tenant:
 *   - Email (SMTP)  → {@code ConnectorService.resolvePlatformSmtpConfig()} (invoices, invites)
 *   - LLM providers → {@code ConnectorService.resolvePlatformProviderApiKey()} (managed model calls)
 *
 * <p>It reuses the same registry catalog, create/config/test endpoints, and
 * {@code ConnectorConfigModalV2} the day-to-day Connectors page uses — no forked connector code.
 * The only difference is ownership: every connector here is stamped to the System Tenant, keeping
 * platform relays cleanly separated from customer connectors.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Plug, ShieldCheck, FlaskConical, Settings, CheckCircle2, AlertCircle,
  Loader, RefreshCw, Plus, Lock, Search, X,
} from 'lucide-react';
import {
  getConnectors, createConnector, listConnectorManifests, getConnectorManifest, testConnector,
} from '../../../services/api';
import ConnectorConfigModalV2 from '../ConnectorConfigModalV2';
import Slideout from '../Slideout';
import { useAuth } from '../../../contexts/AuthContext';

/* Well-known platform-owned scope. Mirrors backend io.agentlets.SystemIds —
 * SYSTEM_ORG_ID / SYSTEM_TENANT_ID are fixed sentinels, so platform connectors
 * always resolve to the same owner regardless of which customer a Platform Admin
 * has switched into. */
const SYSTEM_ORG_ID = '00000000-0000-0000-0000-000000000010';
const SYSTEM_TENANT_ID = '00000000-0000-0000-0000-000000000011';

const norm = (s) => (s || '').trim().toLowerCase();

/* Build a minimal manifest object ConnectorConfigModalV2 needs from a full registry
 * manifest (it renders manifest.configSchema as the typed form). The registry keeps the
 * schema under auth.configSchema; fall back to a top-level configSchema if present. */
const toModalManifest = (full, fallbackName) => ({
  id: full?.id || 'connector',
  name: full?.displayName || fallbackName || 'Connector',
  displayName: full?.displayName || fallbackName || 'Connector',
  authType: full?.auth?.type || 'none',
  baseUrl: full?.metadata?.baseUrl,
  configSchema: full?.auth?.configSchema || full?.configSchema || [],
});

export default function PlatformConnectorsTab() {
  const auth = useAuth();
  const isPlatformAdmin = auth?.session?.role === 'Platform Admin';

  const [connectors, setConnectors] = useState([]);
  const [catalog, setCatalog] = useState([]); // registry manifest summaries for the Add picker
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [testState, setTestState] = useState({}); // connectorId -> { status, msg }

  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerQuery, setPickerQuery] = useState('');
  const [addingId, setAddingId] = useState(null); // manifest id being added

  const [configFor, setConfigFor] = useState(null);       // connector being configured
  const [configManifest, setConfigManifest] = useState(null);

  const reload = useCallback(async () => {
    // Non-Platform-Admins never load platform-scoped connectors (the surface is gated below too).
    if (!isPlatformAdmin) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const [connRes, mfRes] = await Promise.all([
        getConnectors({ tenantId: SYSTEM_TENANT_ID }),
        listConnectorManifests(),
      ]);
      if (connRes.ok) setConnectors(Array.isArray(connRes.data) ? connRes.data : []);
      else setError(connRes.error || 'Failed to load platform connectors');
      if (mfRes.ok) setCatalog(Array.isArray(mfRes.data) ? mfRes.data : (mfRes.data?.manifests || []));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [isPlatformAdmin]);

  useEffect(() => { reload(); }, [reload]);

  // Index the catalog so a connector row can be matched back to its manifest (for the config schema).
  const catalogIndex = useMemo(() => {
    const byId = new Map();
    for (const m of catalog) {
      if (m?.id) byId.set(norm(m.id), m);
      (m?.legacyIds || []).forEach((lid) => byId.set(norm(lid), m));
      if (m?.displayName) byId.set(norm(m.displayName), m);
    }
    return byId;
  }, [catalog]);

  const manifestSummaryForConnector = (c) =>
    catalogIndex.get(norm(c.type)) || catalogIndex.get(norm(c.name)) || null;

  const openConfig = async (c) => {
    // Resolve the connector's manifest (for the typed config schema). Fall back to a bare
    // apiKey form (ConnectorConfigModalV2's default) when no manifest matches.
    const summary = manifestSummaryForConnector(c);
    let full = null;
    if (summary?.id) {
      const res = await getConnectorManifest(summary.id);
      if (res.ok) full = res.data;
    }
    setConfigManifest(toModalManifest(full, c.name));
    setConfigFor(c);
  };

  const handleAdd = async (summary) => {
    setAddingId(summary.id);
    setError(null);
    try {
      // Pull the full manifest for the auth type + config schema.
      const mfRes = await getConnectorManifest(summary.id);
      const full = mfRes.ok ? mfRes.data : null;
      const authType = full?.auth?.type || 'api_key';
      const res = await createConnector({
        name: summary.displayName || summary.id,
        description: summary.description || `Platform-owned ${summary.displayName || summary.id} connector.`,
        type: summary.id,               // manifest id — same value customer connectors use, so resolvers match
        authMethod: authType,
        icon: summary.icon || null,
        status: 'inactive',
        // Owned by the platform System Tenant, not the current customer scope.
        ownerOrgId: SYSTEM_ORG_ID,
        ownerTenantId: SYSTEM_TENANT_ID,
      });
      if (res.ok && res.data) {
        setPickerOpen(false);
        setPickerQuery('');
        await reload();
        setConfigManifest(toModalManifest(full, summary.displayName));
        setConfigFor(res.data); // configure immediately
      } else {
        setError(res.error || `Failed to add ${summary.displayName || summary.id}`);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setAddingId(null);
    }
  };

  const handleTest = async (c) => {
    setTestState((s) => ({ ...s, [c.id]: { status: 'testing' } }));
    try {
      const res = await testConnector(c.id);
      const statusText = res?.data?.status || '';
      const ok = res.ok && (statusText ? /connected|success|ok|active/i.test(statusText) : true);
      setTestState((s) => ({
        ...s,
        [c.id]: { status: ok ? 'ok' : 'fail', msg: res?.data?.message || res?.error || '' },
      }));
      reload();
    } catch (e) {
      setTestState((s) => ({ ...s, [c.id]: { status: 'fail', msg: e.message } }));
    }
  };

  if (!isPlatformAdmin) {
    return (
      <div className="p-8 text-center text-v2-ink-secondary">
        <Lock size={20} className="mx-auto mb-2 text-v2-ink-tertiary" />
        <div className="text-sm font-semibold text-v2-ink">Platform Admin only</div>
        <div className="text-[12px] mt-1">Platform connectors are managed by the platform operator.</div>
      </div>
    );
  }

  // A platform connector is "usable" only once it carries a credential (hasApiKey) or has been
  // configured + tested — the row shows that, not a premature "Connected".
  const usability = (c) => {
    const t = testState[c.id];
    if (t?.status === 'ok') return { bg: '#ecfdf5', text: '#065f46', border: '#a7f3d0', label: 'Tested OK' };
    if (t?.status === 'fail') return { bg: '#fff1f2', text: '#9f1239', border: '#fecdd3', label: 'Test failed' };
    if (c.hasApiKey || /connected|active/i.test(c.status || '')) {
      return { bg: '#eff6ff', text: '#1e40af', border: '#bfdbfe', label: 'Configured' };
    }
    return { bg: '#f8fafc', text: '#475569', border: '#e2e8f0', label: 'Needs setup' };
  };

  const filteredCatalog = catalog
    .filter((m) => {
      if (!pickerQuery.trim()) return true;
      const q = norm(pickerQuery);
      return norm(m.displayName).includes(q) || norm(m.vendor).includes(q)
        || norm(m.category).includes(q) || norm(m.id).includes(q);
    })
    .sort((a, b) => (a.displayName || a.id).localeCompare(b.displayName || b.id));

  return (
    <div style={{ padding: '20px 28px 32px' }}>
      {/* Explainer — what this surface is for, in plain language. */}
      <div style={{
        display: 'flex', gap: 10, alignItems: 'flex-start',
        padding: '12px 16px', marginBottom: 16, borderRadius: 10,
        background: '#f0f9ff', border: '1px solid #bae6fd', color: '#0c4a6e',
      }}>
        <ShieldCheck size={16} style={{ marginTop: 2, flexShrink: 0 }} />
        <div style={{ fontSize: 12, lineHeight: 1.55 }}>
          <strong>Platform-owned connectors.</strong> These belong to the platform, not to any
          customer. Prove7 uses them across platform functions — sending invoices from{' '}
          <span style={{ fontFamily: "'IBM Plex Mono', monospace" }}>billing@prove7.ai</span>,
          invitations into new customers, and managed model calls on the platform's own key. A
          customer's own connectors are never used for this, and these are never used to act on a
          customer's behalf.
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 800, color: '#1c1917' }}>Platform connectors</h3>
        <button onClick={reload} disabled={loading} title="Refresh" style={{
          marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6,
          border: '1px solid #e7e5e4', background: '#fff', color: '#44403c',
          borderRadius: 8, padding: '6px 12px', fontSize: 11, fontWeight: 600, cursor: 'pointer',
        }}>
          <RefreshCw size={12} style={{ animation: loading ? 'acpSpin 1s linear infinite' : 'none' }} />
          Refresh
        </button>
        <button onClick={() => setPickerOpen(true)} style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          background: '#0d9488', color: '#fff', border: 'none',
          borderRadius: 8, padding: '7px 13px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
        }}>
          <Plus size={13} /> Add platform connector
        </button>
      </div>

      {error && (
        <div style={{
          display: 'flex', gap: 8, alignItems: 'center',
          padding: '10px 14px', marginBottom: 12, borderRadius: 8,
          background: '#fff1f2', border: '1px solid #fecdd3', color: '#9f1239', fontSize: 12,
        }}>
          <AlertCircle size={14} /> {error}
        </div>
      )}

      {loading && !connectors.length && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#78716c', fontSize: 13, padding: 12 }}>
          <Loader size={14} style={{ animation: 'acpSpin 1s linear infinite' }} /> Loading platform connectors…
        </div>
      )}

      {!loading && connectors.length === 0 && (
        <div style={{
          background: '#fff', border: '1px dashed #d6d3d1', borderRadius: 12,
          padding: '24px', textAlign: 'center',
        }}>
          <Plug size={22} style={{ color: '#a8a29e', margin: '0 auto 8px' }} />
          <div style={{ fontSize: 13, fontWeight: 700, color: '#1c1917' }}>No platform connectors yet</div>
          <div style={{ fontSize: 12, color: '#78716c', margin: '6px auto 14px', maxWidth: 480, lineHeight: 1.5 }}>
            Add a connector from the marketplace to use across platform functions — for example an
            Email (SMTP) relay for invoices and invitations, or an Anthropic key for managed model calls.
          </div>
          <button onClick={() => setPickerOpen(true)} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: '#0d9488', color: '#fff', border: 'none',
            borderRadius: 8, padding: '9px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
          }}>
            <Plus size={13} /> Add platform connector
          </button>
        </div>
      )}

      {connectors.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {connectors.map((c) => {
            const tone = usability(c);
            const test = testState[c.id];
            const summary = manifestSummaryForConnector(c);
            return (
              <div key={c.id} style={{
                background: '#fff', border: '1px solid #e7e5e4', borderRadius: 12,
                padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14,
              }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 10, background: '#e3f4f1',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <Plug size={19} style={{ color: '#0d9488' }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#1c1917' }}>{c.name}</span>
                    <span style={{
                      fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em',
                      padding: '3px 8px', borderRadius: 6,
                      background: tone.bg, color: tone.text, border: `1px solid ${tone.border}`,
                    }}>{tone.label}</span>
                  </div>
                  <div style={{ fontSize: 11, color: '#78716c', marginTop: 3 }}>
                    {(summary?.category || summary?.vendor || c.type || 'connector')} · owned by the platform (System Tenant)
                  </div>
                  {test && test.status !== 'testing' && (
                    <div style={{
                      marginTop: 6, fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 5,
                      color: test.status === 'ok' ? '#065f46' : '#9f1239',
                    }}>
                      {test.status === 'ok' ? <CheckCircle2 size={12} /> : <AlertCircle size={12} />}
                      {test.status === 'ok'
                        ? 'Test passed'
                        : (() => {
                            // The pill already says the test failed; strip any redundant
                            // "Connection test failed:" prefix so we don't say "failed" twice.
                            const reason = (test.msg || '').replace(/^(connection test failed:\s*)+/i, '').trim();
                            return reason ? `Test failed — ${reason}` : 'Test failed';
                          })()}
                    </div>
                  )}
                </div>
                <button onClick={() => handleTest(c)}
                  disabled={test?.status === 'testing'}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    border: '1px solid #e7e5e4', background: '#fff', color: '#44403c',
                    borderRadius: 8, padding: '7px 13px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                  }}>
                  {test?.status === 'testing'
                    ? <Loader size={13} style={{ animation: 'acpSpin 1s linear infinite' }} />
                    : <FlaskConical size={13} />}
                  Test
                </button>
                <button onClick={() => openConfig(c)} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  background: '#0d9488', color: '#fff', border: 'none',
                  borderRadius: 8, padding: '7px 13px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
                }}>
                  <Settings size={13} /> Configure
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Add-connector picker — the full marketplace catalog, searchable.
          Slideout per the design rule (G378), not a modal. */}
      <Slideout
        isOpen={pickerOpen}
        onClose={() => { setPickerOpen(false); setPickerQuery(''); }}
        width={560}
        ariaLabel="Add a platform connector"
        header={<h2 style={{ fontSize: 15, fontWeight: 800, margin: 0, color: '#1c1917' }}>Add a platform connector</h2>}
      >
        <div>
          <div style={{ fontSize: 12, color: '#57534e', marginBottom: 12, lineHeight: 1.5 }}>
            Pick any connector from the marketplace. It's created owned by the platform (System Tenant)
            and opened for configuration. The ones that belong here are the connectors platform functions
            use — Email, LLM providers, Secrets, IAM.
          </div>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12,
            border: '1px solid #e7e5e4', borderRadius: 8, padding: '7px 10px',
          }}>
            <Search size={14} style={{ color: '#a8a29e' }} />
            <input
              autoFocus
              value={pickerQuery}
              onChange={(e) => setPickerQuery(e.target.value)}
              placeholder="Search connectors by name, vendor, or category…"
              style={{ border: 'none', outline: 'none', flex: 1, fontSize: 13, color: '#1c1917' }}
            />
            {pickerQuery && (
              <button onClick={() => setPickerQuery('')} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#a8a29e' }}>
                <X size={14} />
              </button>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {filteredCatalog.length === 0 && (
              <div style={{ fontSize: 12, color: '#78716c', padding: 12, textAlign: 'center' }}>
                No connectors match “{pickerQuery}”.
              </div>
            )}
            {filteredCatalog.map((m) => (
              <button
                key={m.id}
                onClick={() => handleAdd(m)}
                disabled={!!addingId}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12, textAlign: 'left', width: '100%',
                  border: '1px solid #eeeded', background: addingId === m.id ? '#f0fdfa' : '#fff',
                  borderRadius: 8, padding: '10px 12px', cursor: addingId ? 'default' : 'pointer',
                }}>
                <div style={{
                  width: 30, height: 30, borderRadius: 8, background: '#f5f5f4', flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Plug size={15} style={{ color: '#78716c' }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#1c1917' }}>{m.displayName || m.id}</div>
                  <div style={{ fontSize: 11, color: '#78716c' }}>
                    {[m.category, m.vendor].filter(Boolean).join(' · ') || m.id}
                  </div>
                </div>
                {addingId === m.id
                  ? <Loader size={14} style={{ animation: 'acpSpin 1s linear infinite', color: '#0d9488' }} />
                  : <Plus size={14} style={{ color: '#0d9488' }} />}
              </button>
            ))}
          </div>
        </div>
      </Slideout>

      {configFor && (
        <ConnectorConfigModalV2
          isOpen={!!configFor}
          connector={configFor}
          manifest={configManifest}
          onClose={() => { setConfigFor(null); setConfigManifest(null); }}
          onSaved={() => { setConfigFor(null); setConfigManifest(null); reload(); }}
        />
      )}
    </div>
  );
}
